// ── Money formatting ─────────────────────────────────────────────────────────
//
// Every amount that crosses the wire from Stripe is an INTEGER IN THE SMALLEST
// CURRENCY UNIT (cents for USD). Formatting it is easy to get subtly wrong —
// dividing by 100 in one place and not another, or rendering a raw float — so
// it lives here and is used by every price, proration and invoice display.
//
// Negative amounts are real and meaningful in billing: a mid-cycle plan change
// produces both a credit for the unused portion of the old plan and a charge
// for the new one, and a downgrade can net out negative (a credit balance the
// customer keeps for future invoices). Those must never be shown as a positive
// charge, so the sign is preserved and rendered explicitly.

/**
 * Format an integer cent amount as currency.
 *
 * @param {number|null|undefined} cents   e.g. 1234 → "$12.34"
 * @param {string} currency               ISO code from Stripe, lowercase ok
 * @returns {string} formatted amount, or "—" when there is nothing to show
 */
export function formatMoney(cents, currency = 'usd') {
  if (cents === null || cents === undefined || Number.isNaN(Number(cents))) return '—';
  const value = Number(cents) / 100;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: String(currency || 'usd').toUpperCase(),
      // Stripe amounts are exact to the cent; never round them away.
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch (_) {
    // Unknown/unsupported currency code — degrade rather than throw.
    return `${value.toFixed(2)} ${String(currency || '').toUpperCase()}`.trim();
  }
}

/**
 * Format a proration total for a confirm dialog.
 *
 * Distinguishes the three genuinely different outcomes, because "you will be
 * charged -$4.00" is not something a user should ever read:
 *   > 0  → an immediate charge
 *   < 0  → an account credit applied to future invoices
 *   = 0  → nothing due now
 */
export function describeProration(cents, currency = 'usd') {
  const n = Number(cents || 0);
  if (n > 0) return { kind: 'charge', amount: formatMoney(n, currency) };
  if (n < 0) return { kind: 'credit', amount: formatMoney(Math.abs(n), currency) };
  return { kind: 'none', amount: formatMoney(0, currency) };
}
